import {
  AppField,
  AppStructureFieldParams,
  FieldChoiceValue,
  SimpleRecord,
} from "@smartsuite/types";
import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import {
  getInternalLinkedApplicationFields,
  getInternalLinkedRecords,
  getLinkedApplicationFields,
  getLinkedRecords,
} from "../../../../../api";
import { useFormContext } from "../../../../../hooks/useFormContext";
import { Fields } from "../../../../../types/fields";
import { getFilterData } from "../components/SelectLinkedRecordModal/SelectLinkedRecordModal.helpers";
import {
  filterOutAlreadyLinkedRecords,
  getFieldsInUse,
  resolveDynamicFilterValues,
} from "../linked-records.helpers";
import { useIsPreview } from "../../../../../hooks/useIsPreview";
import { useFormikContext } from "formik";
import {
  FormConfigApplicationStructureItem,
  FormConfigItem,
  LinkedRecordObject,
} from "../../../../../types/form";
import { UseFormFieldResult } from "../../../../../hooks/useFormField";

interface UseLinkedRecordsProps {
  field: FormConfigApplicationStructureItem;
  formField: UseFormFieldResult<LinkedRecordObject[]>;
  onRecordsChange?: (record: SimpleRecord[]) => void;
  formItem: FormConfigItem;
  enabled?: boolean;
}

export interface UseLinkedRecordsResult {
  records?: SimpleRecord[];
  /**
   * Records that can still be linked: `records` without the ones already linked through a single
   * entry backlink field. Use it for anything the user picks from; `records` stays complete so
   * lookups by id (prefill hydration, saved values) keep working.
   */
  selectableRecords?: SimpleRecord[];
  isRecordsLoading: boolean;
  linkedApplicationFields:
    | {
        [key: string]: AppField<AppStructureFieldParams<unknown, FieldChoiceValue>>;
      }
    | undefined;
  linkedAppStructureLoading: boolean;
  isMultiple: boolean;
  isPreview: boolean;
  searchValue: string;
  setSearchValue: React.Dispatch<React.SetStateAction<string>>;
}

export const useLinkedRecords = ({
  field,
  formItem,
  onRecordsChange,
  enabled = true,
}: UseLinkedRecordsProps): UseLinkedRecordsResult => {
  const [searchValue, setSearchValue] = useState("");

  const formContext = useFormContext();
  const { values } = useFormikContext();
  const { isInternal, reportId } = formContext;

  const { isPreview } = useIsPreview();

  const isMultiple = field.params?.entries_allowed === "multiple";

  const isTable =
    formContext.config.feature_flags?.form_linked_record_table_display === true &&
    formItem.params?.display_type === "table";

  const filterData = useMemo(() => {
    const rawFilter = getFilterData(formItem.params, field.params);
    if (!rawFilter) return rawFilter;

    const formValues = values as { [key: string]: Array<{ id: string }> };
    return resolveDynamicFilterValues(rawFilter, formValues);
  }, [JSON.stringify(values), JSON.stringify(formItem.params), JSON.stringify(field.params)]);

  const { data: records, isLoading: recordsLoading } = useQuery(
    `${Fields.linkedrecordfield}_${field.slug}_${
      field.params?.linked_application
    }_records_${JSON.stringify(filterData)}`,
    async () => {
      if (
        formContext.accountId &&
        (isInternal ? reportId : formContext.config.sharing_hash) &&
        field.params?.linked_application
      ) {
        const fieldsInUse = getFieldsInUse(formItem, field, filterData, isTable);

        const res =
          (await (isInternal && reportId
            ? getInternalLinkedRecords(
                formContext.accountId,
                reportId,
                field.params.linked_application,
                field.slug,
                fieldsInUse,
                filterData
              )
            : getLinkedRecords(
                formContext.accountId,
                formContext.config.sharing_hash,
                field.params.linked_application,
                field.slug,
                fieldsInUse,
                filterData,
                isPreview,
                formContext.passcode
              ))) || [];

        onRecordsChange?.(res);

        return res;
      }
    },
    {
      enabled,
      staleTime: Infinity,
    }
  );

  const { data: linkedApplicationFields, isLoading: linkedAppStructureLoading } = useQuery(
    `${Fields.linkedrecordfield}_${field.slug}_${field.params?.linked_application}_fields_labels`,
    async () => {
      if (
        formContext.accountId &&
        (isInternal ? reportId : formContext.config.sharing_hash) &&
        field.params?.linked_application
      ) {
        const fieldsInUse = getFieldsInUse(formItem, field, filterData, isTable);

        return await (isInternal && reportId
          ? getInternalLinkedApplicationFields(
              formContext.accountId,
              reportId,
              field.params.linked_application,
              fieldsInUse
            )
          : getLinkedApplicationFields(
              formContext.accountId,
              field.params.linked_application,
              formContext.config.sharing_hash,
              fieldsInUse,
              isPreview,
              formContext.passcode
            ));
      }
    },
    {
      staleTime: Infinity,
    }
  );

  const selectableRecords = useMemo(
    () => records && filterOutAlreadyLinkedRecords({ records, field, linkedApplicationFields }),
    [records, field, linkedApplicationFields]
  );

  return {
    records,
    selectableRecords,
    isRecordsLoading: recordsLoading,
    linkedApplicationFields,
    linkedAppStructureLoading,
    searchValue,
    isMultiple,
    isPreview,
    setSearchValue,
  };
};
